export type BookFormatId =
  | 'comic-issue'
  | 'graphic-novel'
  | 'manga'
  | 'kids-comic'
  | 'anthology'
  | 'webcomic-print';

export type ComicStyleId =
  | 'classic-comic'
  | 'modern-cinematic'
  | 'manga-bw'
  | 'manga-color'
  | 'clear-line'
  | 'cartoon'
  | 'indie-graphic'
  | 'noir'
  | 'retro-pulp'
  | 'sci-fi';

export type CharacterStyleId =
  | 'semi-realistic'
  | 'stylized-cartoon'
  | 'anime-inspired'
  | 'chibi'
  | 'graphic-realism'
  | 'retro-pulp'
  | 'cute-mascot'
  | 'anthropomorphic'
  | 'clean-line';

export type PrintMode = 'black-white' | 'standard-color' | 'premium-color';
export type BindingMode = 'paperback' | 'hardcover';

export type TrimPreset = {
  id: string;
  label: string;
  widthIn: number;
  heightIn: number;
  popular?: boolean;
  category: 'kdp' | 'industry-custom';
  paperback: boolean;
  hardcover: boolean;
  standardColor: boolean;
  premiumColor: boolean;
  blackWhite: boolean;
  note?: string;
};

export const bookFormats = [
  {
    id: 'comic-issue' as const,
    label: 'Comic issue',
    description: 'Short serialized issue with strong page turns.',
    recommendedTrim: '7x10',
    recommendedStyle: 'classic-comic',
    recommendedCharacterStyle: 'semi-realistic',
  },
  {
    id: 'graphic-novel' as const,
    label: 'Graphic novel',
    description: 'Long-form sequential storytelling for print.',
    recommendedTrim: '7x10',
    recommendedStyle: 'modern-cinematic',
    recommendedCharacterStyle: 'semi-realistic',
  },
  {
    id: 'manga' as const,
    label: 'Manga',
    description: 'Compact, panel-dense storytelling; color or black-and-white.',
    recommendedTrim: '5x8',
    recommendedStyle: 'manga-bw',
    recommendedCharacterStyle: 'anime-inspired',
  },
  {
    id: 'kids-comic' as const,
    label: 'Kids comic',
    description: 'Bold readable panels, expressive characters and larger type.',
    recommendedTrim: '8x10',
    recommendedStyle: 'cartoon',
    recommendedCharacterStyle: 'stylized-cartoon',
  },
  {
    id: 'anthology' as const,
    label: 'Anthology',
    description: 'Multiple stories or creators in one volume.',
    recommendedTrim: '7x10',
    recommendedStyle: 'indie-graphic',
    recommendedCharacterStyle: 'semi-realistic',
  },
  {
    id: 'webcomic-print' as const,
    label: 'Webcomic → print',
    description: 'Adapt an existing digital/web series into a printed book.',
    recommendedTrim: '7x10',
    recommendedStyle: 'modern-cinematic',
    recommendedCharacterStyle: 'stylized-cartoon',
  },
];

export const comicStyles = [
  {
    id: 'classic-comic' as const,
    label: 'Classic comic',
    description: 'Bold inks, clear silhouettes, energetic panel storytelling.',
    prompt:
      'classic sequential comic language, confident ink contours, bold readable shapes, dynamic action staging',
  },
  {
    id: 'modern-cinematic' as const,
    label: 'Modern cinematic',
    description: 'Cinematic framing, dramatic light and contemporary polish.',
    prompt:
      'modern cinematic comic illustration, controlled detail, dramatic lighting, strong visual hierarchy',
  },
  {
    id: 'manga-bw' as const,
    label: 'Manga — B&W',
    description: 'Black-and-white line art, screentone-ready values, kinetic panels.',
    prompt:
      'black and white manga-inspired sequential art, expressive linework, strong value design, print-friendly tones',
  },
  {
    id: 'manga-color' as const,
    label: 'Manga — color',
    description: 'Manga-inspired compositions with full-color production.',
    prompt:
      'color manga-inspired sequential art, expressive clean linework, polished color separation',
  },
  {
    id: 'clear-line' as const,
    label: 'Clear-line adventure',
    description: 'Clean consistent outlines, flat colors and highly readable scenes.',
    prompt:
      'clear-line comic illustration, consistent clean contours, flat color separation, highly readable environments',
  },
  {
    id: 'cartoon' as const,
    label: 'Cartoon / all ages',
    description: 'Friendly shapes, expressive faces and simplified detail.',
    prompt:
      'friendly cartoon comic illustration, expressive readable faces, simplified clean forms, playful visual rhythm',
  },
  {
    id: 'indie-graphic' as const,
    label: 'Indie graphic novel',
    description: 'Editorial composition, restrained palette and personal tone.',
    prompt:
      'independent graphic-novel illustration, thoughtful composition, tactile linework, restrained cohesive palette',
  },
  {
    id: 'noir' as const,
    label: 'Noir',
    description: 'High contrast, shadow-heavy compositions and dramatic staging.',
    prompt:
      'noir comic illustration, high-contrast lighting, graphic shadows, moody cinematic composition',
  },
  {
    id: 'retro-pulp' as const,
    label: 'Retro pulp',
    description: 'Vintage print energy without imitating a specific artist.',
    prompt:
      'retro pulp-comic visual language, vintage print energy, bold halftone-inspired texture, period color sensibility',
  },
  {
    id: 'sci-fi' as const,
    label: 'Sci-fi cinematic',
    description: 'Detailed technology, atmospheric scale and clean storytelling.',
    prompt:
      'cinematic science-fiction comic illustration, readable technology design, atmospheric scale, clean panel storytelling',
  },
];

export const characterStyles = [
  {
    id: 'semi-realistic' as const,
    label: 'Semi-realistic',
    description: 'Believable anatomy with expressive comic simplification.',
    prompt: 'semi-realistic comic characters, believable anatomy, expressive facial acting',
  },
  {
    id: 'stylized-cartoon' as const,
    label: 'Stylized cartoon',
    description: 'Strong silhouettes, exaggerated expression and readable poses.',
    prompt: 'stylized cartoon characters, strong silhouettes, exaggerated readable expressions',
  },
  {
    id: 'anime-inspired' as const,
    label: 'Anime-inspired',
    description: 'Clean expressive features and graphic hair/clothing shapes.',
    prompt: 'anime-inspired character design, clean expressive features, graphic readable silhouettes',
  },
  {
    id: 'chibi' as const,
    label: 'Chibi',
    description: 'Compact proportions and highly expressive faces.',
    prompt: 'chibi character proportions, oversized expressive head, compact body, clear simple costume shapes',
  },
  {
    id: 'graphic-realism' as const,
    label: 'Graphic realism',
    description: 'More realistic features while remaining clearly illustrated.',
    prompt: 'graphic-realistic comic characters, natural proportions, illustrated texture, controlled facial detail',
  },
  {
    id: 'retro-pulp' as const,
    label: 'Retro pulp',
    description: 'Period comic character shapes and bold costume readability.',
    prompt: 'retro pulp-inspired character design, bold period silhouettes, graphic costume shapes',
  },
  {
    id: 'cute-mascot' as const,
    label: 'Cute mascot',
    description: 'Simple memorable shapes optimized for recurring characters.',
    prompt: 'cute mascot character design, memorable simple silhouette, expressive eyes, repeatable proportions',
  },
  {
    id: 'anthropomorphic' as const,
    label: 'Anthropomorphic',
    description: 'Animal characters with expressive human-like posing.',
    prompt: 'anthropomorphic character design, species-recognizable features, expressive human-like body language',
  },
  {
    id: 'clean-line' as const,
    label: 'Clean line',
    description: 'Minimal, repeatable line design for strong consistency.',
    prompt: 'clean-line character design, minimal repeatable details, strong visual identity, simple color blocking',
  },
];

export const trimPresets: TrimPreset[] = [
  {
    id: '5x8',
    label: '5 × 8 in — Compact',
    widthIn: 5,
    heightIn: 8,
    popular: true,
    category: 'kdp',
    paperback: true,
    hardcover: false,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '5.06x7.81',
    label: '5.06 × 7.81 in',
    widthIn: 5.06,
    heightIn: 7.81,
    category: 'kdp',
    paperback: true,
    hardcover: false,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '5.25x8',
    label: '5.25 × 8 in',
    widthIn: 5.25,
    heightIn: 8,
    category: 'kdp',
    paperback: true,
    hardcover: false,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '5.5x8.5',
    label: '5.5 × 8.5 in — Digest',
    widthIn: 5.5,
    heightIn: 8.5,
    popular: true,
    category: 'kdp',
    paperback: true,
    hardcover: true,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '6x9',
    label: '6 × 9 in — Trade',
    widthIn: 6,
    heightIn: 9,
    popular: true,
    category: 'kdp',
    paperback: true,
    hardcover: true,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '6.14x9.21',
    label: '6.14 × 9.21 in',
    widthIn: 6.14,
    heightIn: 9.21,
    category: 'kdp',
    paperback: true,
    hardcover: true,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '6.69x9.61',
    label: '6.69 × 9.61 in',
    widthIn: 6.69,
    heightIn: 9.61,
    category: 'kdp',
    paperback: true,
    hardcover: false,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '7x10',
    label: '7 × 10 in — Graphic comic',
    widthIn: 7,
    heightIn: 10,
    popular: true,
    category: 'kdp',
    paperback: true,
    hardcover: true,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
    note: 'Recommended default for graphic comics in this studio.',
  },
  {
    id: '7.44x9.69',
    label: '7.44 × 9.69 in',
    widthIn: 7.44,
    heightIn: 9.69,
    category: 'kdp',
    paperback: true,
    hardcover: false,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '7.5x9.25',
    label: '7.5 × 9.25 in',
    widthIn: 7.5,
    heightIn: 9.25,
    category: 'kdp',
    paperback: true,
    hardcover: false,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '8x10',
    label: '8 × 10 in — Large',
    widthIn: 8,
    heightIn: 10,
    popular: true,
    category: 'kdp',
    paperback: true,
    hardcover: false,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '8.25x6',
    label: '8.25 × 6 in — Landscape',
    widthIn: 8.25,
    heightIn: 6,
    category: 'kdp',
    paperback: true,
    hardcover: false,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '8.25x8.25',
    label: '8.25 × 8.25 in — Square',
    widthIn: 8.25,
    heightIn: 8.25,
    popular: true,
    category: 'kdp',
    paperback: true,
    hardcover: false,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '8.5x8.5',
    label: '8.5 × 8.5 in — Square',
    widthIn: 8.5,
    heightIn: 8.5,
    category: 'kdp',
    paperback: true,
    hardcover: false,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '8.5x11',
    label: '8.5 × 11 in — US Letter',
    widthIn: 8.5,
    heightIn: 11,
    popular: true,
    category: 'kdp',
    paperback: true,
    hardcover: false,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: '8.27x11.69',
    label: '8.27 × 11.69 in — A4',
    widthIn: 8.27,
    heightIn: 11.69,
    popular: true,
    category: 'kdp',
    paperback: true,
    hardcover: false,
    standardColor: false,
    premiumColor: true,
    blackWhite: true,
    note: 'Standard color is not available for this KDP paperback size.',
  },
  {
    id: '8.25x11-hardcover',
    label: '8.25 × 11 in — Hardcover large',
    widthIn: 8.25,
    heightIn: 11,
    category: 'kdp',
    paperback: false,
    hardcover: true,
    standardColor: false,
    premiumColor: true,
    blackWhite: true,
  },
  {
    id: 'us-comic-custom',
    label: '6.625 × 10.25 in — Traditional U.S. comic',
    widthIn: 6.625,
    heightIn: 10.25,
    popular: true,
    category: 'industry-custom',
    paperback: true,
    hardcover: false,
    standardColor: true,
    premiumColor: true,
    blackWhite: true,
    note: 'Industry-style custom trim; validate in KDP preview before publication.',
  },
];

export function bleedPageSize(widthIn: number, heightIn: number) {
  return {
    widthIn: Number((widthIn + 0.125).toFixed(3)),
    heightIn: Number((heightIn + 0.25).toFixed(3)),
  };
}

export function isLargeTrim(widthIn: number, heightIn: number) {
  return widthIn > 6.12 || heightIn > 9;
}

export function minPageCount(
  binding: BindingMode,
  printMode: PrintMode
) {
  if (binding === 'hardcover') return 75;
  return printMode === 'standard-color' ? 72 : 24;
}
